import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { NavigationLinksConfig } from '@core/config/navigation-links.config';
import { ProductName } from '@core/constants';
import { PlatformService } from '@core/services/platform.service';
import { LocalStorageService, SecurityService } from '@mynexus/mynexus-ui-lib';
import { HeaderLinksConfig } from '@shared/models/links-config-model';
import { filter } from 'rxjs';

@Component({
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    standalone: false
})
export class MainComponent implements OnInit {
  platformService = inject(PlatformService);
  authenticationService = inject(MsalService);
  securityService = inject(SecurityService);
  localStorage = inject(LocalStorageService);
  router = inject(Router);
  destroyRef = inject(DestroyRef);

  userName = this.authenticationService.instance?.getActiveAccount()?.name || '';

  navLinks: HeaderLinksConfig[] = [];

  isLoading = true;
  defaultProduct!: ProductName | null;

  async ngOnInit() {
    // Fetch App Critical data
    await this.platformService.getLineOfBusinesses();
    this.defaultProduct = await this.platformService.getDefaultProduct();

    this.isLoading = false;

    this.platformService.currentProduct.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe((product) => {
      this.navLinks = this.getNavLinks(product);
    });

    this.platformService.currentProductChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((product) => {
      this.navLinks = this.getNavLinks(product);

      const currentUrl = this.router.url;
      const baseRoute = this.navLinks[0].url;

      // temp solution for better rules component context switching
      // we need to destroy the component to get new context and inject new service
      // destroy is happening by navigation to any other route
      if (currentUrl.includes('rules')) {
        this.router
          .navigateByUrl('/access-denied', { skipLocationChange: true })
          .then(() => this.router.navigate(['rules']));
      } else {
        this.router.navigate([baseRoute]);
      }
    });

    this.setInitialRoute();
  }

  logout(): void {
    this.localStorage.clear();
    this.authenticationService.logout();
  }

  private setInitialRoute() {
    if (!this.defaultProduct) {
      this.router.navigateByUrl('/access-denied');

      return;
    }

    const currentUrl = this.router.url;
    const baseAppRoute = this.navLinks[0].url;

    if (currentUrl === '/') {
      this.router.navigate([baseAppRoute]);
    }
  }

  private getNavLinks(product: ProductName) {
    return NavigationLinksConfig[product].filter((link) => this.securityService.hasAccess(link.permissions)) || [];
  }
}
